import { UnsupportedCase } from '../../angular/classes/UnsupportedUseCases';
import { BaseQuote } from '../../../common/js/quote/BaseQuote';
import { RateClasses } from '../../../common/js/static/RateClasses';
import { BusinessUnit } from '../../../common/js/customer/Agent';
import { FlatExtraResponse, Rider } from '../../../common/js/productService/ProductServiceClient';
import { FlatExtra } from '../../../common/js/customer/FlatExtra';
import { ViewAddress } from '../../../common/js/interfaces/ExamScheduler';
import { CoverpathAnnuity } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathAnnuity';
import { UseCaseWithMessage } from '../../angular/classes/UseCaseWithMessage';
import { Address } from '../../../common/js/interfaces/CommonTypes';
import { Agent } from '../../../common/js/customer/Agent';
import { Policy } from '../../../common/js/customer/Policy';
import type { Data as RouterData } from '@angular/router';

export enum ApplicationTypes {
  PRIOR = 'prior',
  DELETED = 'deleted',
  /* "Disabled" application type can only be used to sunset bad data, for example: data patches.
     Do not remove even if it appears this application type is not used in the code.
  */
  DISABLED = 'disabled',
  ACTIVE = 'active',
}

export type WorkflowStateType = {
  state: string;
  subState: string;
};

export type ProductQuoteResponse = {
  base: number;
  waiver: number;
  riders?: Array<Rider>;
  flatExtras?: Array<FlatExtraResponse>;
  annualBaseRates?: Array<number>;
  annualWaiverRates?: Array<number>;
  totalTargetPremium?: number;
  annualBase?: number;
};

export type ProductQuoteResponseList = {
  responses: Array<ProductQuoteResponse>;
};

export type ClientCustomerProps = {
  quote?: {};
  quoteType?: string;
  person: {
    email: string;
    firstName: string;
    lastName: string;
    age?: number;
  };
  address: {
    state: string;
  };
  progress: number;
  primaryQuote?: BaseQuote;
  oldEmail?: string;
};

export type HelpContactInfo = {
  name: string;
  email: string;
  phoneLink: string;
  phone: string;
  policies: Policy[];
};

export type PrimaryAgentAndPolicies = {
  agent: Agent;
  policies: Policy[];
};

export type CoverpathCustomerProps = ClientCustomerProps & {
  person: {
    email: string;
    firstName: string;
    lastName: string;
    age?: number;
    tfaPhone?: string;
  };
  tfaRequired: boolean;
  isAgentAssistant?: boolean;
  product?: string;
  productType?: string;
  termQuote?: BaseQuote;
  paidSource?: string;
  paidSource_cid?: string;
  paidSource_pdata?: string;
  paidSource_campaign?: string;
};

export enum ModalType {
  generic,
  esign,
  agentShareLink,
  loadingTOAdvisorAssistantApplication,
  applicationPreviouslySent,
  changeVerificationPhone,
  premiumBreakdown,
  scheduleMedicalExam,
  convertPolicy,
  changeDividendOption,
  cancelPolicy,
  cancelPolicySimple,
  applicationAlreadyEsignedByAdvisor,
  applicationAlreadyCanceledByClientWhenAdvisorEsign,
  unsupportedCases,
  useCaseWithMessage,
  agentSubmission,
  agentSubmissionFailed,
  completeApplicationForm,
  agentSendApplicaton,
  advisorProcessingApplicationForm,
  policyDocRetrievalFailed,
  paymentIfMissing,
  paymentIfMissingTlic,
  bankInfo,
  bankInfoJointSplit,
  beneWidget,
  beneWidgetJointSplit,
  contactWidget,
  contactWidgetJointSplit,
  policyDocsJointSplit,
  policyReplacement,
  policyReplacementThankYou,
  accountAlreadyExists,
  previewInvitation,
  changeExamVendor,
  contactWidgetMmDirect,
  deleteApplication,
  cpAgentLoading,
  amend,
  sendAmend,
  sendAmendPreview,
  sendAmendFromListView,
  unauthorized,
  attestation,
  documentUpload,
  clientEsign,
  amendWholeLifeOrJointApp,
  editPhoneNumber,
  groupReplacement,
  signerEmail,
  opsProxyLogin,
  switchProducer,
  declineTLICConfirm,
  ALIR1035,
  ALIR1035Removed,
  CashValueReplacementRemoved,
  confirmAppDuplication,
  otherCompanyReplacementWarning,
  premiumLessQuote,
  premiumLessCommonQuote,
  esignModal,
  paymentModal,
  checkLegalGuardian,
  confirmLegalGuardian,
  editSignerName,
  changeSanderlingDetail,
  advisorAppIncomplete,
  teleInterviewerSubmission,
  clientConversionEsign,
  AuthorizePayment,
  lockedQuestionIncomplete,
  productSwitch,
  null,
}

export type ModalConfig = {
  type: ModalType;
  notShowClose?: boolean;
  disableEscapeKeyPress?: boolean;
  title?: string;
  body?: string;
  actions?;
  unsupportedCase?: UnsupportedCase;
  extraClasses?: string;
  policyType?: string;
  modalData?: object;
  backgroundClass?: string;
  useCaseWithMessage?: UseCaseWithMessage;
  unsupportedCaseData?: string;
};

export type AgentProfile = {
  email: string;
  firstName: string;
  lastName: string;
  producerId: string;
  agencyId: string;
  businessUnit?: BusinessUnit;
  group?: string;
  groups?: Array<string>;
  displayName?: string;
};

/**
 * shortDescription:    a short description for health class used by Coverpath UI
 * longDescription:     a long & detailed description for health class used by Coverpath UI and basic illustration
 * tobaccoDescription:  a tobacco usage focused health class description used by Coverpath UI and basic illustration
 * healthClassNumber:   an internal number used for converting health class used by quick quote page
 */
export type RateClassDescriptionType = {
  shortDescription?: string;
  longDescription?: string;
  tobaccoDescription?: string;
  healthClassNumber?: number;
  title?: string;
};

/**
 * Descriptive information about ratings used in CP Quotes
 *
 * @type {{[p: string]: RateClassDescriptionType}}
 */
export const CPQuoteRateClassDescriptions = {
  [RateClasses.ULTRA.stringCode]: <RateClassDescriptionType>{
    title: 'Ultra Preferred',
    shortDescription: 'excellent health',
    longDescription: 'Ultra Preferred, Non-Tobacco (Excellent Health)',
    tobaccoDescription: 'Ultra Preferred Non-Tobacco',
    healthClassNumber: 0,
  },
  [RateClasses.SELECT.stringCode]: <RateClassDescriptionType>{
    title: 'Select Preferred',
    shortDescription: 'very good health',
    longDescription: 'Select Preferred, Non-Tobacco (Very Good Health)',
    tobaccoDescription: 'Select Preferred Non-Tobacco',
    healthClassNumber: 1,
  },
  [RateClasses.STANDARD.stringCode]: <RateClassDescriptionType>{
    title: 'Standard',
    shortDescription: 'good health',
    longDescription: 'Standard, Non-Tobacco (Good Health)',
    tobaccoDescription: 'Non-Tobacco',
    healthClassNumber: 2,
  },
  [RateClasses.SELECT_TOBACCO.stringCode]: <RateClassDescriptionType>{
    title: 'Select Preferred Tobacco',
    shortDescription: 'good health',
    longDescription: 'Select Preferred, Tobacco (Good Health)',
    tobaccoDescription: 'Select Preferred Tobacco',
    healthClassNumber: 2,
  },
  [RateClasses.STANDARD_TOBACCO.stringCode]: <RateClassDescriptionType>{
    title: 'Standard Tobacco',
    shortDescription: 'average health',
    longDescription: 'Standard, Tobacco (Average Health)',
    tobaccoDescription: 'Tobacco',
    healthClassNumber: 3,
  },
  [RateClasses.TABLE_B.stringCode]: <RateClassDescriptionType>{
    title: 'Table B',
    shortDescription: 'Table B',
    longDescription: 'Table B',
    tobaccoDescription: 'Non-Tobacco',
    healthClassNumber: 60.102, //can be any number as it is only used by quick quote UI, for convenience, making it same as the rate class number in RateClasses.ts
  },
  [RateClasses.TABLE_B_TOBACCO.stringCode]: <RateClassDescriptionType>{
    title: 'Table B, Tobacco',
    shortDescription: 'Table B, Tobacco',
    longDescription: 'Table B, Tobacco',
    tobaccoDescription: 'Tobacco',
    healthClassNumber: 70.102,
  },
  [RateClasses.TABLE_C.stringCode]: <RateClassDescriptionType>{
    title: 'Table C',
    shortDescription: 'Table C',
    longDescription: 'Table C',
    tobaccoDescription: 'Non-Tobacco',
    healthClassNumber: 60.103,
  },
  [RateClasses.TABLE_C_TOBACCO.stringCode]: <RateClassDescriptionType>{
    title: 'Table C, Tobacco',
    shortDescription: 'Table C, Tobacco',
    longDescription: 'Table C, Tobacco',
    tobaccoDescription: 'Tobacco',
    healthClassNumber: 70.103,
  },
  [RateClasses.TABLE_D.stringCode]: <RateClassDescriptionType>{
    title: 'Table D',
    shortDescription: 'Table D',
    longDescription: 'Table D',
    tobaccoDescription: 'Non-Tobacco',
    healthClassNumber: 60.104,
  },
  [RateClasses.TABLE_D_TOBACCO.stringCode]: <RateClassDescriptionType>{
    title: 'Table D, Tobacco',
    shortDescription: 'Table D, Tobacco',
    longDescription: 'Table D, Tobacco',
    tobaccoDescription: 'Tobacco',
    healthClassNumber: 70.104,
  },
  [RateClasses.TABLE_E.stringCode]: <RateClassDescriptionType>{
    title: 'Table E',
    shortDescription: 'Table E',
    longDescription: 'Table E',
    tobaccoDescription: 'Non-Tobacco',
    healthClassNumber: 60.105,
  },
  [RateClasses.TABLE_E_TOBACCO.stringCode]: <RateClassDescriptionType>{
    title: 'Table E, Tobacco',
    shortDescription: 'Table E, Tobacco',
    longDescription: 'Table E, Tobacco',
    tobaccoDescription: 'Tobacco',
    healthClassNumber: 70.105,
  },
  [RateClasses.TABLE_F.stringCode]: <RateClassDescriptionType>{
    title: 'Table F',
    shortDescription: 'Table F',
    longDescription: 'Table F',
    tobaccoDescription: 'Non-Tobacco',
    healthClassNumber: 60.106,
  },
  [RateClasses.TABLE_F_TOBACCO.stringCode]: <RateClassDescriptionType>{
    title: 'Table F, Tobacco',
    shortDescription: 'Table F, Tobacco',
    longDescription: 'Table F, Tobacco',
    tobaccoDescription: 'Tobacco',
    healthClassNumber: 70.106,
  },
  [RateClasses.TABLE_H.stringCode]: <RateClassDescriptionType>{
    title: 'Table H',
    shortDescription: 'Table H',
    longDescription: 'Table H',
    tobaccoDescription: 'Non-Tobacco',
    healthClassNumber: 60.108,
  },
  [RateClasses.TABLE_H_TOBACCO.stringCode]: <RateClassDescriptionType>{
    title: 'Table H, Tobacco',
    shortDescription: 'Table H, Tobacco',
    longDescription: 'Table H, Tobacco',
    tobaccoDescription: 'Tobacco',
    healthClassNumber: 70.108,
  },
  [RateClasses.TABLE_J.stringCode]: <RateClassDescriptionType>{
    title: 'Table J',
    shortDescription: 'Table J',
    longDescription: 'Table J',
    tobaccoDescription: 'Non-Tobacco',
    healthClassNumber: 60.11,
  },
  [RateClasses.TABLE_J_TOBACCO.stringCode]: <RateClassDescriptionType>{
    title: 'Table J, Tobacco',
    shortDescription: 'Table J, Tobacco',
    longDescription: 'Table J, Tobacco',
    tobaccoDescription: 'Tobacco',
    healthClassNumber: 70.11,
  },
  [RateClasses.TABLE_L.stringCode]: <RateClassDescriptionType>{
    title: 'Table L',
    shortDescription: 'Table L',
    longDescription: 'Table L',
    tobaccoDescription: 'Non-Tobacco',
    healthClassNumber: 60.112,
  },
  [RateClasses.TABLE_L_TOBACCO.stringCode]: <RateClassDescriptionType>{
    title: 'Table L, Tobacco',
    shortDescription: 'Table L, Tobacco',
    longDescription: 'Table L, Tobacco',
    tobaccoDescription: 'Tobacco',
    healthClassNumber: 70.112,
  },
  [RateClasses.TABLE_P.stringCode]: <RateClassDescriptionType>{
    title: 'Table P',
    shortDescription: 'Table P',
    longDescription: 'Table P',
    tobaccoDescription: 'Non-Tobacco',
    healthClassNumber: 60.116,
  },
  [RateClasses.TABLE_P_TOBACCO.stringCode]: <RateClassDescriptionType>{
    title: 'Table P, Tobacco',
    shortDescription: 'Table P, Tobacco',
    longDescription: 'Table P, Tobacco',
    tobaccoDescription: 'Tobacco',
    healthClassNumber: 70.116,
  },
};

/**
 * Descriptive information about ratings for CP policies (a superset of CPQuoteRateClassDescriptions)
 *
 * @type {{[p: string]: RateClassDescriptionType}}
 */

export const CPPolicyRateClassDescriptions = Object.assign({}, CPQuoteRateClassDescriptions, {
  [RateClasses.TABLE_B.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating B',
    longDescription: 'Non-Tobacco, Table Rating B',
    healthClassNumber: Number(RateClasses.TABLE_B),
    tobaccoDescription: 'Non-Tobacco',
  },
  [RateClasses.TABLE_B_TOBACCO.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating B',
    longDescription: 'Tobacco, Table Rating B',
    healthClassNumber: Number(RateClasses.TABLE_B_TOBACCO),
    tobaccoDescription: 'Tobacco',
  },
  [RateClasses.TABLE_C.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating C',
    longDescription: 'Non-Tobacco, Table Rating C',
    healthClassNumber: Number(RateClasses.TABLE_C),
    tobaccoDescription: 'Non-Tobacco',
  },
  [RateClasses.TABLE_C_TOBACCO.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating C',
    longDescription: 'Tobacco, Table Rating C',
    healthClassNumber: Number(RateClasses.TABLE_C_TOBACCO),
    tobaccoDescription: 'Tobacco',
  },
  [RateClasses.TABLE_D.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating D',
    longDescription: 'Non-Tobacco, Table Rating D',
    healthClassNumber: Number(RateClasses.TABLE_D),
    tobaccoDescription: 'Non-Tobacco',
  },
  [RateClasses.TABLE_D_TOBACCO.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating D',
    longDescription: 'Tobacco, Table Rating D',
    healthClassNumber: Number(RateClasses.TABLE_D_TOBACCO),
    tobaccoDescription: 'Tobacco',
  },
  [RateClasses.TABLE_E.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating E',
    longDescription: 'Non-Tobacco, Table Rating E',
    healthClassNumber: Number(RateClasses.TABLE_E),
    tobaccoDescription: 'Non-Tobacco',
  },
  [RateClasses.TABLE_E_TOBACCO.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating E',
    longDescription: 'Tobacco, Table Rating E',
    healthClassNumber: Number(RateClasses.TABLE_E_TOBACCO),
    tobaccoDescription: 'Tobacco',
  },
  [RateClasses.TABLE_F.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating F',
    longDescription: 'Non-Tobacco, Table Rating F',
    healthClassNumber: Number(RateClasses.TABLE_F),
    tobaccoDescription: 'Non-Tobacco',
  },
  [RateClasses.TABLE_F_TOBACCO.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating F',
    longDescription: 'Tobacco, Table Rating F',
    healthClassNumber: Number(RateClasses.TABLE_F_TOBACCO),
    tobaccoDescription: 'Tobacco',
  },
  [RateClasses.TABLE_H.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating H',
    longDescription: 'Non-Tobacco, Table Rating H',
    healthClassNumber: Number(RateClasses.TABLE_H),
    tobaccoDescription: 'Non-Tobacco',
  },
  [RateClasses.TABLE_H_TOBACCO.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating H',
    longDescription: 'Tobacco, Table Rating H',
    healthClassNumber: Number(RateClasses.TABLE_H_TOBACCO),
    tobaccoDescription: 'Tobacco',
  },
  [RateClasses.TABLE_J.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating J',
    longDescription: 'Non-Tobacco, Table Rating J',
    healthClassNumber: Number(RateClasses.TABLE_J),
    tobaccoDescription: 'Non-Tobacco',
  },
  [RateClasses.TABLE_J_TOBACCO.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating J',
    longDescription: 'Tobacco, Table Rating J',
    healthClassNumber: Number(RateClasses.TABLE_J_TOBACCO),
    tobaccoDescription: 'Tobacco',
  },
  [RateClasses.TABLE_L.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating L',
    longDescription: 'Non-Tobacco, Table Rating L',
    healthClassNumber: Number(RateClasses.TABLE_L),
    tobaccoDescription: 'Non-Tobacco',
  },
  [RateClasses.TABLE_L_TOBACCO.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating L',
    longDescription: 'Tobacco, Table Rating L',
    healthClassNumber: Number(RateClasses.TABLE_L_TOBACCO),
    tobaccoDescription: 'Tobacco',
  },
  [RateClasses.TABLE_P.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating P',
    longDescription: 'Non-Tobacco, Table Rating P',
    healthClassNumber: Number(RateClasses.TABLE_P),
    tobaccoDescription: 'Non-Tobacco',
  },
  [RateClasses.TABLE_P_TOBACCO.stringCode]: <RateClassDescriptionType>{
    shortDescription: 'Table Rating P',
    longDescription: 'Tobacco, Table Rating P',
    healthClassNumber: Number(RateClasses.TABLE_P_TOBACCO),
    tobaccoDescription: 'Tobacco',
  },
});

/**
 * Health class dropdown used by CP UI for the five basic health classes.
 * An array of arrays. Each inner array is len of 2
 *
 * @type [[rate class code, rate class description], [rate class code, rate class description], ...]
 */
export const UIHealthClassOptionsBasic = [
  [RateClasses.ULTRA.stringCode, CPQuoteRateClassDescriptions[RateClasses.ULTRA.stringCode].longDescription],
  [RateClasses.SELECT.stringCode, CPQuoteRateClassDescriptions[RateClasses.SELECT.stringCode].longDescription],
  [RateClasses.STANDARD.stringCode, CPQuoteRateClassDescriptions[RateClasses.STANDARD.stringCode].longDescription],
  [
    RateClasses.SELECT_TOBACCO.stringCode,
    CPQuoteRateClassDescriptions[RateClasses.SELECT_TOBACCO.stringCode].longDescription,
  ],
  [
    RateClasses.STANDARD_TOBACCO.stringCode,
    CPQuoteRateClassDescriptions[RateClasses.STANDARD_TOBACCO.stringCode].longDescription,
  ],
];

/**
 * Health class dropdown used by CP UI for table ratings (substandard).
 * An array of arrays. Each inner array is len of 2
 *
 * @type [[rate class code, rate class description], [rate class code, rate class description], ...]
 */
export const UIHealthClassOptionsTableRating = [
  [RateClasses.TABLE_B.stringCode, CPQuoteRateClassDescriptions[RateClasses.TABLE_B.stringCode].longDescription],
  [
    RateClasses.TABLE_B_TOBACCO.stringCode,
    CPQuoteRateClassDescriptions[RateClasses.TABLE_B_TOBACCO.stringCode].longDescription,
  ],
  [RateClasses.TABLE_C.stringCode, CPQuoteRateClassDescriptions[RateClasses.TABLE_C.stringCode].longDescription],
  [
    RateClasses.TABLE_C_TOBACCO.stringCode,
    CPQuoteRateClassDescriptions[RateClasses.TABLE_C_TOBACCO.stringCode].longDescription,
  ],
  [RateClasses.TABLE_D.stringCode, CPQuoteRateClassDescriptions[RateClasses.TABLE_D.stringCode].longDescription],
  [
    RateClasses.TABLE_D_TOBACCO.stringCode,
    CPQuoteRateClassDescriptions[RateClasses.TABLE_D_TOBACCO.stringCode].longDescription,
  ],
  [RateClasses.TABLE_E.stringCode, CPQuoteRateClassDescriptions[RateClasses.TABLE_E.stringCode].longDescription],
  [
    RateClasses.TABLE_E_TOBACCO.stringCode,
    CPQuoteRateClassDescriptions[RateClasses.TABLE_E_TOBACCO.stringCode].longDescription,
  ],
  [RateClasses.TABLE_F.stringCode, CPQuoteRateClassDescriptions[RateClasses.TABLE_F.stringCode].longDescription],
  [
    RateClasses.TABLE_F_TOBACCO.stringCode,
    CPQuoteRateClassDescriptions[RateClasses.TABLE_F_TOBACCO.stringCode].longDescription,
  ],
  [RateClasses.TABLE_H.stringCode, CPQuoteRateClassDescriptions[RateClasses.TABLE_H.stringCode].longDescription],
  [
    RateClasses.TABLE_H_TOBACCO.stringCode,
    CPQuoteRateClassDescriptions[RateClasses.TABLE_H_TOBACCO.stringCode].longDescription,
  ],
  [RateClasses.TABLE_J.stringCode, CPQuoteRateClassDescriptions[RateClasses.TABLE_J.stringCode].longDescription],
  [
    RateClasses.TABLE_J_TOBACCO.stringCode,
    CPQuoteRateClassDescriptions[RateClasses.TABLE_J_TOBACCO.stringCode].longDescription,
  ],
  [RateClasses.TABLE_L.stringCode, CPQuoteRateClassDescriptions[RateClasses.TABLE_L.stringCode].longDescription],
  [
    RateClasses.TABLE_L_TOBACCO.stringCode,
    CPQuoteRateClassDescriptions[RateClasses.TABLE_L_TOBACCO.stringCode].longDescription,
  ],
  [RateClasses.TABLE_P.stringCode, CPQuoteRateClassDescriptions[RateClasses.TABLE_P.stringCode].longDescription],
  [
    RateClasses.TABLE_P_TOBACCO.stringCode,
    CPQuoteRateClassDescriptions[RateClasses.TABLE_P_TOBACCO.stringCode].longDescription,
  ],
];

/**
 * Health class dropdown used by CP UI, including all basic and table ratings (substandard).
 * An array of arrays. Each inner array is len of 2
 *
 * @type [[rate class code, rate class description], [rate class code, rate class description], ...]
 */
export const UIHealthClassOptions = UIHealthClassOptionsBasic.concat(UIHealthClassOptionsTableRating);

/**
 * Health class dropdown used by CP UI for non-tobacco table ratings (substandard).
 * An array of arrays. Each inner array is len of 2
 *
 * @type [[rate class code, rate class description], [rate class code, rate class description], ...]
 */
export const UIHealthClassOptionsNonTobaccoTableRating = UIHealthClassOptionsTableRating.filter(
  (option) => option[0].indexOf('_TOBACCO') === -1
);

/**
 * Health class dropdown used by CP UI for juveniles, including one basic standard and all non-tobacco table ratings (substandard).
 * An array of arrays. Each inner array is len of 2
 *
 * @type [[rate class code, rate class description], [rate class code, rate class description], ...]
 */
export const UIHealthClassOptionsJuvenile = [
  [RateClasses.STANDARD.stringCode, CPQuoteRateClassDescriptions[RateClasses.STANDARD.stringCode].longDescription],
].concat(UIHealthClassOptionsNonTobaccoTableRating);

export const TermOptions = {
  '10': '10',
  '15': '15',
  '20': '20',
  '25': '25',
  '30': '30',
  '15 ECP': '15 ECP',
  '20 ECP': '20 ECP',
  '25 ECP': '25 ECP',
  '30 ECP': '30 ECP',
};

export type ChangeExamVendor = {
  examVendor?: string;
};

export const FeatureFlags = {
  wholeLife65: 'wholeLife65',
  agentProxy: 'agentProxy',
  use2019Filing: 'use2019Filing',
  exchange1035: '1035Exchange',
  isJuvenilePolicyEnabled: 'isJuvenilePolicyEnabled',
  partialConversionEnabled: 'partialConversionEnabled',
  artEnabled: 'artEnabled',
  AAAAEnabled: 'AAAAEnabled',
  isParamedStatusEnabled: 'isParamedStatusEnabled',
  aacFilterEnabled: 'aacFilterEnabled',
  decisionReasonsEnabled: 'decisionReasonsEnabled',
  lowFaceWholeLifeEnabled: 'lowFaceWholeLifeEnabled',
  nwmReplacementsEnabled: 'nwmReplacementsEnabled',
  lowerDividendRateEnabled: 'lowerDividendRateEnabled',
  editAppEnabled: 'editAppEnabled',
  isBackDatingAmendmentEnabled: 'isBackDatingAmendmentEnabled',
  isDocuSignRedirectEnabled: 'isDocuSignRedirectEnabled',
  earlyInsuredConsentEnabled: 'earlyInsuredConsentEnabled',
  excludeJointFromOR1: 'excludeJointFromOR1',
  annuity: 'annuity',
  annuityNAICBestInterestStates: 'annuity-naic-best-interest',
  unsupportedStatesForAnnuity: 'annuity-unsupported-states',
  liveDataEnabled: 'liveDataEnabled',
  routerWithBusinessCases: 'routerWithBusinessCases',
  alirScheduled: 'alirScheduled',
  alir1035NoPremium: 'alir1035NoPremium',
  alir1035Premium: 'alir1035Premium',
  cirEnabled: 'CirEnabled',
  cirQuoteBlocked: 'cirQuoteBlocked',
  brokerExpansionEnabled: 'brokerExpansionEnabled',
  quoteTableRatingEnabled: 'quoteTableRatingEnabled',
  isProductSwitchingEnabled: 'isProductSwitchingEnabled',
  inForceIllustrationEnabled: 'inForceIllustrationEnabled',
  nonroundedFaceAmountsEnabled: 'nonroundedFaceAmountsEnabled',
  isQuotePageV2Enabled: 'isQuotePageV2Enabled',
  isCoverpathNYEnabled: 'isCoverpathNYEnabled',
  isAccepttoEventTrackingEnabled: 'isAccepttoEventTrackingEnabled',
  isAccepttoAuthAndCheckEnabled: 'accepttoauthandcheck',
  displaySanderling: 'displaySanderling',
  annuityRulesEngineVersion: 'annuity-rules-engine-version',
  isSanderlingConversionEnabled: 'isSanderlingConversionEnabled',
  enableCRMDataVerification: 'enableCRMDataVerification',
  isBrokerExceptionsEnabled: 'isBrokerExceptionsEnabled',
  // TODO HL-45294: Remove refs to isCoverpath10PayEnabled feature flag once new WL 10 Pay (CPL10A) goes live
  isCoverpath10PayEnabled: 'isCoverpath10PayEnabled',
  coverpath10PayPolicyExceptions: 'coverpath10PayPolicyExceptions',
  coverpath7702WLPolicyExceptions: 'coverpath7702WLPolicyExceptions',
  annuityFormVersioning: 'annuity-form-versioning',
  batchApiControlConfig: 'batchApiControlConfig',
  enableRichCaseExport: 'enableRichCaseExport',
  conceptillustrationsv2: 'conceptillustrationsv2',
  producerGroupsEnabled: 'producerGroupsEnabled',
  listViewCachingEnabled: 'listViewCachingEnabled',
  changeOwnerAddressEnabled: 'changeOwnerAddressEnabled',
  isAgentSubmittedAppEnabled: 'isAgentSubmittedAppEnabled',
  previewTermRepriceDate: 'previewTermRepriceDate',
  showTermProductPremiumVersion: 'showTermProductPremiumVersion',
  sanderlingPolicyRepairEnabled: 'sanderlingPolicyRepairEnabled',
  sanderlingPolicyTypes: 'sanderlingPolicyTypes',
  isConversionToExistingWLEnabled: 'isConversionToExistingWLEnabled',
  isTeleCMIEnabled: 'isTeleCMIEnabled',
  currentRateYear: 'CurrentRateYear',
  isCoverpath7702Enabled: 'isCoverpath7702Enabled',
  tlicOptionalityEnabled: 'tlicOptionalityEnabled',
  annuitiesApplicationVersion: 'annuities-application-version',
  annuitiesSuitabilityVersion: 'annuities-suitability-version',
  changePaymentFrequencyEnabled: 'changePaymentFrequencyEnabled',
  casrecProductApiVersion: 'casrecProductApiVersion',
  casreProductApiVersion: 'casreProductApiVersion',
  cassvProductApiVersion: 'cassvProductApiVersion',
  caseLevelVisibilityForAE: 'caseLevelVisibilityForAE',
  enableConversionAutomation: 'enableConversionAutomation',
  enableLimitedCoverpath2023conversions: 'enableLimitedCoverpath2023conversions',
  mmClientGiactWebDebitVerificationServiceEnabled: 'mmClientGiactWebDebitVerificationServiceEnabled',
  mmAgentGiactWebDebitVerificationServiceEnabled: 'mmAgentGiactWebDebitVerificationServiceEnabled',
  autoFollowSettingEnabled: 'autoFollowSettingEnabled',
  enableNewListViewQuery: 'enableNewListViewQuery',
  emailFooterSurveyEnabled: 'emailFooterSurveyEnabled',
  enableWholesalerIFI: 'enableWholesalerIFI',
  enableWLIllustrationDropDown: 'enableWLIllustrationDropDown',
  enableWLFaceIncreaseIllustration: 'enableWLFaceIncreaseIllustration',
  examOptOutEnabled: 'examOptOutEnabled',
  enableAACQueryLogs: 'enableAACQueryLogs',
  caseRedesignEnabled: 'caseRedesignEnabled',
  disableDiffIOconversion: 'disableDiffIOconversion',
  alertSecGroup: 'alertSecGroup',
  enableGenesysCobrowse: 'enableGenesysCobrowse',
  isDocuSignOAuthEnabled: 'isDocuSignOAuthEnabled',
  altSubmitSignVerbiageEnabled: 'altSubmitSignVerbiageEnabled',
  isQuestionLockEnabled: 'isQuestionLockEnabled',
  enableMultipleAdvisorsOneCustomer: 'enableMultipleAdvisorsOneCustomer',
  isAppProductSwitchEnabled: 'isAppProductSwitchEnabled',
  enableAuthPayforDDB: 'enableAuthPayforDDB',
  urgentCaseFilterEnabled: 'urgentCaseFilterEnabled',
  enableNewHelpCenter: 'enableNewHelpCenter',
  enableLimitedCoverpath2023: 'enableLimitedCoverpath2023',
  enableSplitJointAppListView: 'enableSplitJointAppListView',
  enableFailedPaymentRetry: 'enableFailedPaymentRetry',
  lowFaceWL10EnableDate: 'lowFaceWL10EnableDate',
  lowFaceWL10OverrideEnabled: 'lowFaceWL10OverrideEnabled',
  enableEventorIntegration: 'enableEventorIntegration',
  docusignClientCancelPowerFormId: 'docusignClientCancelPowerFormId',
  fieldnetIllustrationLink: 'fieldnetIllustrationLink',
  exportQuoteIllustration: 'exportQuoteIllustration',
  nbDecommission: 'new-business-decommission',
  nbDecommissionBanner: 'new-business-decommission-banner',
  nbDecommissionBannerText: 'new-business-decommission-banner-text',
  nbDecommissionPost30Days: 'new-business-decommission-post-30-days',
  cpClientCancelTermPolicyPowerFormId: 'cpClientCancelTermPolicyPowerFormId',
  cpClientChangeFaceAmountPowerFormId: 'cpClientChangeFaceAmountPowerFormId',
  cpClientChangeNamePowerFormId: 'cpClientChangeNamePowerFormId',
  cpClientChangeContactInfoPowerFormId: 'cpClientChangeContactInfoPowerFormId',
};

export enum PartyType {
  owner = 'owner',
  insured = 'insured',
  payer = 'payer',
  signer = 'signer',
  otherOwner = 'otherOwner',
  contingentOwner = 'contingentOwner',
}

export const PaymentMode = {
  ach: 'ACH',
  check: 'Direct Bill',
};

export enum ValidatedOpsOrWholesaleProxyFunctions {
  SET_AGENT = 'setAgent',
}

export enum ValidatedProxyFunctions {
  CONFIRM_ATTESTATION = 'confirmAttestation',
}

export enum ValidatedAmendmentsFunctions {
  SAVE_AMENDED_VALUES = 'saveAmendmentValues',
  SEND_AMENDED_POLICY = 'fullFillSendAmendedPolicyToCustomerRequirement',
  DUPLICATE_APPLICATION = 'duplicateApplication',
}

export enum ValidatedControllerFunctions {
  SAVE_EXAM_VENDOR = 'saveExamVendor',
  DUPLICATE_APPLICATION = 'duplicateApplication',
  SAVE_ANIMATION_VIEW_DATE = 'saveAnimationViewDate',
}

export enum ValidatedExamSchedulingFunctions {
  CREATE_EXAM_SCHEDULE_ORIGINATOR = 'createExamScheduleOriginator',
}

export enum ValidatedCommissionSplitFunctions {
  SET_DEFAULT_PRODUCER_COMMISSION = 'setDefaultProducerCommissions',
}

export enum CoverpathURLs {
  ACCOUNT_CENTER = '/account-center',
  DETAILED_VIEW = '/client-details',
  PRODUCER_GROUP = '/producer-group',
  ILLUSTRATIONS = '/illustrations',
  QUOTE = '/home',
  SETTINGS = '/settings',
  CASE_DETAILS = '/case-details',
  PAGE_NOT_FOUND = '**',
  QUOTE_OR_APPLY = '/home/quote-or-apply',
}

export enum LoanType {
  VARIABLE = 'Variable',
  FIXED = 'Fixed',
}
export type RemoteControllerFunctions =
  | ValidatedProxyFunctions
  | ValidatedControllerFunctions
  | ValidatedCommissionSplitFunctions;

export type ClientSearchInfo = {
  customerId: string;
  email: string;
  ownerFirstName: string;
  ownerLastName: string;
  contractState: string;
  ownerClassification: string;
  ownerEntityName: string;
  ownerEntityType: string;
  ownerTrusteeType: string;
  ownerTrusteeEntityName: string;
};

export type DividendOptionSet =
  | 'Paid-Up Additions'
  | 'Reduced Premium/PUAs'
  | 'Cash'
  | 'Reduced Premium/Cash'
  | 'Accumulate at Interest';

export type DividendOptionMap = {
  PUA: 'Paid-Up Additions';
  PRPUA: 'Reduced Premium/PUAs';
  Cash: 'Cash';
  PRCash: 'Reduced Premium/Cash';
  Accumulate: 'Accumulate at Interest';
};

export type ConvertPolicyPayload = {
  dividendOption: DividendOptionSet;
  faceAmount: number;
  loanOption: LoanType;
  paymentFrequency: string;
  product: string;
  taxBracket: number;
  premium: number;
  waiverOfPremium: boolean;
  waiver: number;
  effectiveDate: Date;
  rateClass: string;
  wholeAPL: boolean;
  conversionAddress: Address;
  invite: string;
  showPremiumToClient: boolean;
  carrierCode: string;
  producerId: string;
};

export type AgentSearchInfo = {
  producerId: string;
  firstName: string;
  lastName: string;
  id: string;
};

export enum LockedByUserType {
  CUSTOMER = 'customer',
  AGENT = 'agent',
  TELE_INTERVIEWER = 'tele-interviewer',
}

export enum SiteType {
  CLIENT_SITE = 'coverpath-client',
  AGENT_SITE = 'coverpath-agent',
}

export enum ReplacementCompanies {
  MassMutual = 'MassMutual',
  NorthwesternMutual = 'Northwestern Mutual',
}

export enum FollowupClosingSources {
  CUSTOMER = 'customer',
  OPS = 'ops',
}

export enum CPMetadataFilingTypes {
  CP2021 = 'Coverpath 2021',
  CP2020 = 'Coverpath 2020',
  CP2019 = 'Coverpath 2019',
  CP2018 = 'Coverpath 2018',
}

//todo:HL-39852 - remove after cutover
export enum VpqSeverityTypes {
  HARDSTOP = 'error',
  SOFTSTOP = 'info',
}

export enum OwnerCategory {
  INDIVIDUAL = 'Individual',
  BUSINESS = 'Business',
  TRUST = 'Trust',
  JOINT = 'Joint',
}

export enum AnnuityOwnerType {
  INDIVIDUAL = 'Individual',
  TRUST = 'Trust',
  CORPORATION = 'Corporation',
}

export enum AnnuityLives {
  SINGLE_LIFE = 'Single Life',
  JOINT_LIFE = 'Joint Life',
  JOINT_LIFE_CONVERTIBLE = 'Joint Life Convertible',
  PERIOD_CERTAIN_ONLY = 'Period Certain Only',
}

export enum AnnuitySurvivorshipOption {
  NO_REDUCTION = 'No Reduction',
  EITHER_ANNUITANT = 'Either Annuitant',
  PRIMARY_ANNUITANT = 'Primary Annuitant',
}

export enum AnnuityReductionAmounts {
  THREE_QUARTERS = 0.75,
  TWO_THIRDS = 0.66,
  HALF = 0.5,
}

export enum AnnuityPaymentSource {
  SAVINGS = 'Savings',
  NON_QUALIFIED = 'Non-Qualified',
  MUTUAL_FUNDS_STOCKS = 'Mutual Funds Stocks',
  IRA_TRADITIONAL = 'IRA Traditional',
  KEOGH = 'Keogh',
  IRA_SEP = 'IRA SEP',
  IRA_CUSTODIAL = 'IRA Custodial',
  TSA = 'TSA',
  NON_QUALIFIED_DEFERRED_COMP = 'Non-Qualified Deferred Compensation',
  QUALIFIED_EMPLOYER_PLAN = 'Qualified Employer Plan',
  IRA_ROTH = 'IRA ROTH',
  GOVT_DEFERRED_COMP = 'Governmental Deferred Compensation',
  EMPLOYER_CONTRIBUTIONS = 'Employer Contributions',
  IRA_SIMPLE = 'IRA SIMPLE',
}

export enum AnnuityMarket {
  NON_QUALIFIED = 'Non-Qualified',
  QLAC = 'QLAC',
  IRA = 'IRA',
  SEP_IRA = 'SEP IRA',
  ROTH_IRA = 'ROTH IRA',
  CUSTODIAL_IRA = 'Custodial IRA',
  QLAC_IRA = 'QLAC IRA',
  CUSTODIAL_QLAC_IRA = 'Custodial QLAC IRA',
  _401K = '401(k)',
  KEOGH = 'Keogh (H.R.10)',
  MONEY_PURCHASE_PENSION_PLAN = 'Money Purchase Pension Plan',
  NON_QUALIFIED_DEFERRED_COMP = 'Non-Qualified Deferred Compensation',
  PENSION_PLAN = 'Pension Plan',
  PROFIT_SHARING_PLAN = 'Profit Sharing Plan',
  TARGET_BENEFIT_PLAN = 'Target Benefit Plan',
  BENEFICIARY_IRA = 'Beneficiary IRA',
}

export enum AnnuityRefundType {
  PERIOD_CERTAIN = 'Period Certain',
  CASH = 'Cash',
  NONE = 'None',
  NO_DEATH_BENEFIT = 'No Death Benefit',
  INSTALLMENT = 'Installment Refund',
}

export enum AnnuityFrequency {
  MONTHLY = 'Monthly',
  QUARTERLY = 'Quarterly',
  SEMI_ANNUAL = 'Semi-Annual',
  ANNUAL = 'Annual',
}

export enum AnnuityInflationProtector {
  NONE = 'None',
  ONE_PERCENT = '1%',
  TWO_PERCENT = '2%',
  THREE_PERCENT = '3%',
  FOUR_PERCENT = '4%',
}

export enum AnnuityProductSelection {
  CASREC = 'CASREC',
  CASRE = 'CASRE',
  CASSV = 'CASSV',
}

export enum AnnuityApplicationMetadataFileName {
  ICC14A1300_AN = 'ICC14A1300-AN',
  ICC21A1300_AN_20210601 = 'ICC21A1300-AN-20210601',
  A1300CT = 'A1300CT',
  A1300ND = 'A1300ND-AN 0621',
  A1300RFA = 'A1300RFA-AN 0518',
  A1300RFA_AN_20210601 = 'A1300RFA-AN 0621',
}

export enum AnnuitySuitabilityMetadataFileName {
  F9726S = 'F9726S',
  F9726S_BI = 'F9726S-BI',
  F9726S_20210625 = 'F9726S-20210625',
  F9726S_BI_20210625 = 'F9726S-BI-20210625',
  F9726S_BI_20210901 = 'F9726S-BI-20210901',
  F9726S_BI_20220201 = 'F9726S-BI-20220201',
}

export enum AnnuityReplacementMetadataFileName {
  REPLACEMENT_20210625 = 'Replacement-20210625',
}

export enum AnnuityCertificateOfTrustMetadataFileName {
  CERTIFICATE_OF_TRUST = 'F6734',
}

export enum AnnuityFR3013MetadataFileName {
  FR3013 = 'FR3013-20220201',
}

export type AnnuityMetadataFileName =
  | AnnuityApplicationMetadataFileName
  | AnnuitySuitabilityMetadataFileName
  | AnnuityReplacementMetadataFileName
  | AnnuityCertificateOfTrustMetadataFileName
  | AnnuityFR3013MetadataFileName;

export const AnnuityMetadataFileName = {
  ...AnnuityApplicationMetadataFileName,
  ...AnnuitySuitabilityMetadataFileName,
  ...AnnuityReplacementMetadataFileName,
  ...AnnuityCertificateOfTrustMetadataFileName,
  ...AnnuityFR3013MetadataFileName,
};

export enum AnnuityGuaranteePeriod {
  THREE_YEAR = '3 year',
  FOUR_YEAR = '4 year',
  FIVE_YEAR = '5 year',
  SEVEN_YEAR = '7 year',
  NINE_YEAR = '9 year',
}

export enum AnnuityPeriodCertainOnlyLives {
  SINGLE_OWNER = 'Single-Owner',
  JOINT_OWNER = 'Joint-Owner',
}

export enum AnnuityRenewalOption {
  TO_MATURITY = 'To maturity',
  SPECIFY_DURATION = 'Specify duration',
}

export enum AnnuityWithdrawalType {
  SPECIFY_AMOUNT = 'Specify Amount',
  SPECIFY_PERCENT = 'Specify % of Contract Value',
  AVAILABLE_FREE_OUT = 'Available free Out',
}

export enum OriginatorActions {
  CREATE_APPLICATION = 'CREATE_APPLICATION',
  SCHEDULE_EXAM = 'SCHEDULE_EXAM',
}

export type WholeLifeQuotePremiumBreakdown = {
  paymentFrequency: string;
  basePremium: number;
  annualBasePremium: number;
  hasWaiver: boolean;
  waiver: number;
  hasGirRider: boolean;
  girPremium: number;
  girWaiver: number;
  hasRtrRider: boolean;
  rtrPremium: number;
  rtrWaiver: number;
  flatExtras: Array<FlatExtra>;
  rtrFlatExtras: Array<FlatExtra>;
  productCode: string;
  alirEnabled: boolean;
  alirAmount: number;
  alirPremium: number;
  hasCirRider: boolean;
  cirPremium: number;
  cirWaiver: number;
  totalPremium: number;
};

export type TermQuotePremiumBreakdown = {
  basePremium: number;
  hasWaiver: boolean;
  waiver: number;
  flatExtras: Array<FlatExtra>;
  coverageTerm: number;
  totalPremium: number;
};

export interface PreServerDataLog {
  status: string;
  version?: number;
  hasChanges?: boolean;
  customerId?: string;
  forceUpdate: boolean;
}

export type ValidateDocumentAccess = {
  polno: string;
  isValid: boolean;
  signerFirstName: string;
  attemptsRemaining: number;
  esignCompleted: boolean;
  urlExpired: boolean;
  recipient?: string;
};

export interface AnnuityAdditionalSignerValidation extends ValidateDocumentAccess {
  recipient: AnnuityAdditionalSigners;
  signerId: string;
}

export type AnnuityAdditionalSigners = 'jointowner' | 'jointannuitant' | 'primaryannuitant';

// NOTE: this is driven by application metadata
// Question: The Trustee further certifies that all documents pertaining to this policy/contract be signed by
// We should find a way to read this list from metadata
export const CertifiedDocumentSigneeMap = {
  all: 'All authorized Trustees',
  majority: 'A majority of authorized Trustees',
  any: 'Any authorized Trustee',
  designated: 'Designated Trustee',
};

export const OwnerEntityTypes = {
  NON_INCORPORATED_ENTITY: 'nonIncorporatedEntity',
  INCORPORATED_ENTITY: 'incorporatedEntity',
  TRUST: 'trust',
  PERSON: null,
};

// NOTE: this is driven by application metadata
// "key": "B00192",
// "question": "Select type",
// We should find a way to read this list from metadata
export const TrustOrOtherEntityTypeMap = {
  trust: 'Trust',
  incorporatedEntity: 'Incorporated Entity',
  nonIncorporatedEntity: 'Non-incorporated Entity',
};

export const UserTypes = {
  PERSON: 'Person', // application form does not define an entity type when owner is a person
  TRUST: TrustOrOtherEntityTypeMap.trust,
  BUSINESS: TrustOrOtherEntityTypeMap.incorporatedEntity,
  NON_INCORPORATED: TrustOrOtherEntityTypeMap.nonIncorporatedEntity,
};

export const UserTitles = {
  OWNER: 'Owner',
  ACCOUNT_HOLDER: 'Account Holder',
  OWNER_ACCOUNT_HOLDER: 'Owner & Account Holder',
  SIGNER_ACCOUNT_HOLDER: 'Signer & Account Holder',
  ANNUITANT: 'Annuitant',
  JOINT_ANNUITANT: 'Joint Annuitant',
};

export type UserInformation = {
  cardTitle: string;
  userType: string; // Person | Trust | Business
  name: string;
  address?: ViewAddress;
  stateFullName?: string;
  email?: string;
  phoneNumber?: string;
  showTFAPhone?: boolean;
  trusteeSignatureOption?: string;
  trustees?: Array<{ name: string; email: string }>;
  canUpdatePhone?: boolean;
  relationship?: string; // Grandparent | Father | Mother ...
};

export type OwnerInformation = {
  ownerTitle: string;
  ownerType: string; // Person | Trust | Business
  name: string;
  address?: Address;
  stateFullName?: string;
  email?: string;
  phoneNumber?: string;
  showTFAPhone?: boolean;
  trusteeSignatureOption?: string;
  trustees?: Array<{ name: string; email: string }>;
};

export type InsuredSignerInformation = {
  name: string;
  relationship: string;
  address?: ViewAddress;
  stateFullName?: string;
  email?: string;
  phoneNumber?: string;
};

// NOTE: this is driven by application metadata
// "key": "B00192",
// "question": "Select type",
export const OwnerTypes = {
  PERSON: 'Person', // application form does not define an entity type when owner is a person
  TRUST: TrustOrOtherEntityTypeMap.trust,
  BUSINESS: TrustOrOtherEntityTypeMap.incorporatedEntity,
  NON_INCORPORATED: TrustOrOtherEntityTypeMap.nonIncorporatedEntity,
};

export const OwnerTitles = {
  OWNER: 'Owner',
  ACCOUNT_HOLDER: 'Account Holder',
  OWNER_ACCOUNT_HOLDER: 'Owner & Account Holder',
  SIGNER_ACCOUNT_HOLDER: 'Signer & Account Holder',
  JOINT_OWNER_ACCOUNT_HOLDER: 'Joint Owner & Account Holder',
  JOINT_OWNER: 'Joint Owner',
};

export enum REPLACEMENT_DOC_INCLUSION_RULE {
  ALWAYS, // requires a replacement doc at all times, regardless of whether app is a replacement
  ONLY_IF_IS_REPLACEMENT, // requires a replacement doc only if app is a replacement
}

export type AnnuityAppDocument = {
  description: string;
  type: string;
  name?: string;
  pdf?: string;
  displayName?: string;
  requiresAdvisorSignature?: boolean;
  requiresOwnerSignature?: boolean;
  requiresJointOwnerSignature?: boolean;
  requiresAnnuitantSignature?: boolean;
  requiresJointAnnuitantSignature?: boolean;
  replacementDocInclusionRule?: REPLACEMENT_DOC_INCLUSION_RULE;
  isEmpty?: boolean; // if true then state does NOT require this document, OR the document is not available during agent e-signing
  isStatic?: boolean; // if true then pdf does NOT require mappings

  // decides whether to include pdf in app packet
  inclusionRule?: (annuity: CoverpathAnnuity) => boolean;
};

export const Exchange1035FundingPurpose = {
  ALIR_ONLY: 'ALIR_ONLY',
  PREMIUM_AND_ALIR: 'PREMIUM_AND_ALIR',
};

export const Exchange1035FundingStatus = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
};

export const Exchange1035PolicyType = {
  universal: 'Universal Life',
  variable: 'Variable Life',
  whole: 'Whole Life',
};

export const ConceptIllustrationVSTErrors = {
  START_AGE: 'StartAgeError',
};

export type PartnerApplicationFillPropertiesMap = {
  'customer.application.primaryPolicy.insured.person.middleName': string;
  'customer.application.primaryPolicy.insured.person.SSN': string;
  'customer.application.primaryPolicy.insured.phone.number': string;
  'customer.application.primaryPolicy.insured.person.email': string;
  'customer.application.primaryPolicy.ownerPerson.middleName': string;
  'customer.application.primaryPolicy.ownerPerson.SSN': string;
  'customer.application.primaryPolicy.ownerPersonPhone.number': string;
  'customer.application.primaryPolicy.ownerPersonResidentialAddress.state': string;
};

export type PartnerAddress = {
  line1: string;
  line2: string;
  line3: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export type PartnerParty = {
  firstName: string;
  middleName: string;
  lastName: string;
  nameSuffix: string;
  phone: string;
  email: string;
  taxId: string;
};

export type PartnerApplicationData = {
  advisorId: string;
  ownerIsInsured: boolean;
  owner: PartnerParty & {
    address: PartnerAddress;
  };
  insured: PartnerParty & {
    mailingAddress: PartnerAddress;
    primaryAddress: PartnerAddress;
    dob: string;
    gender: string;
  };
};

export enum Partners {
  A360 = 'A360',
  Barnum = 'Barnum',
}

export enum VPQStage {
  SEND_QUOTE = 'SendQuote',
  ISSUE_POLICY = 'IssuePolicy',
}

export enum VPQFailureSeverity {
  ERROR = 'error',
  WARNING = 'warning',
}

export enum VPQFailureType {
  SYSTEM_DOWN = 'SYSTEM_DOWN', //Possible DSP outage
  SYSTEM_ERROR = 'SYSTEM_ERROR', // DSP system error
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS', // Something wrong with producer credentials
  EXPIRED_AML_CERT = 'EXPIRED_AML_CERT', // Producer's AMP certification is expired
  STALE_LICENSE = 'STALE_LICENSE', // Producer's license is stale
  UNAUTHORIZED_AGENCY = 'UNAUTHORIZED_AGENCY', // Unauthorized agency
  INVALID_APPOINTMENT = 'INVALID_APPOINTMENT', // Producer's appointment is invalid
  UNEXPECTED_RESPONSE = 'UNEXPECTED_RESPONSE', // Response from DSP is not in the expected format and CDM doesn't know how to handle it
  INVALID = 'INVALID',
}

export enum CDMVPQMockExample {
  VPQ_NO_ERROR = 'vpq-no-failure',
  VPQ_FAILURE_SYSTEM_DOWN = 'vpq-failure-system-down',
  VPQ_FAILURE_SYSTEM_ERROR = 'vpq-failure-system-error',
  VPQ_FAILURE_INVALID_CREDS = 'vpq-failure-invalid-creds',
  VPQ_FAILURE_STALE_LICENSE = 'vpq-failure-stale-license',
  VPQ_FAILURE_INVALID_APT = 'vpq-failure-invalid-appointment',
  VPQ_FAILURE_EXPIRED_AML = 'vpq-failure-expired-aml',
  VPQ_FAILURE_EXPIRED_AML_AND_APPOINTMENT = 'vpq-failure-expired-aml-and-appointment',
  VPQ_FAILURE_UNAUTHORIZED_AGENCY = 'vpq-failure-unauthorized-agency',
  VPQ_FAILURE_TRAINING_REQUIRED = 'vpq-failure-training-required',
  VPQ_FAILURE_UNEXPECTED_RESPONSE = 'vpq-failure-unexpected-response',
  VPQ_FAILURE_INVALID = 'vpq-failure-invalid',
}

export enum CPAccepttoEventNames {
  DUPLICATE_APP = 'DuplicateApp',
  VIEW_APP = 'ViewApp',
  DOWNLOAD_POLICY = 'DownloadPolicy',
  DOWNLOAD_CASE_CSV = 'DownloadCaseCSV',
}

export enum CPAccepttoEventMessages {
  DUPLICATE_APP = 'Duplicate Application',
  VIEW_APP = 'View Application',
  DOWNLOAD_POLICY = 'Download Policy Packet',
  DOWNLOAD_CASE_CSV = 'Download Cases',
}

export interface AMCSSOPayload {
  isLoggedIn?: boolean;
  clientID?: string;
  domain?: string;
  connection?: string;
  audience?: string;
}

export enum InviteErrors {
  SESSION_COLLISION = 'Unable to claim invitation while another session is active. Expiring existing session. Please try link again.',
  GENERIC = 'There was an error in parsing invite code',
  AAA_QUOTE_EMAIL_MISMATCH = 'aaa_quote_email_mismatch',
}

export interface GiactWebDebitResponse {
  status: string;
  normalizedResponseCode: string;
  description?: string;
  sourceResponseCode: string;
  statusCode?: string;
  id?: string;
}

export interface GiactWebDebitRequest {
  referenceId: string;
  referenceType: string;
  productCode: string;
  userSource: string;
  channel: string;
  routingNumber: string;
  accountNumber: string;
}

export enum GiactResponseCodes {
  SERVICE_OFF = 'SERVICE_OFF',
  INVALID = 'INVALID',
}

export enum Entitlement {
  AGENT = 'AGENT',
  AGENT_PROXY = 'AGENT_PROXY',
  OPS_PROXY = 'OPS_PROXY',
  WHOLESALE_PROXY = 'WHOLESALE_PROXY',
  DUAL_ROLE = 'DUAL_ROLE',
  TELE_INTERVIEWER = 'TELE_INTERVIEWER',
}

export interface AgentError {
  producerId: string;
  severity: VPQFailureSeverity;
  errorMessage: string;
}

export interface AutocompleteOption {
  listValue: string;
  fieldValue: string;
}

export type DetailedLocalAgent = {
  email: string;
  entitlement: string;
  lastUpdatedTime: string;
  agentProfile: AgentProfile;
};

export type DateDMY = {
  year: number;
  month: number;
  day: number;
};
export enum NonForfeitureOptions {
  RPU = 'Reduced Paid-Up',
  ETI = 'Extended Term Insurance',
}

export enum ConversionPaymentMode {
  ACH = 'ach',
}

export type PaymentInfoPayload = {
  paymentInfo: {
    payerName: string;
    bankAccountNumber: string;
    bankRoutingNumber: string;
    accountType: string;
    effectiveDraftDate: string;
  };
};
export interface Illustration {
  illustrationId: string;
  productType: string;
  insuredId: string;
  faceAmount: number;
  product: string;
  healthClass: string;
  waiverOfPremium: boolean;
  dividendOption: string;
  dividendInterestAdjustment: number;
  taxBracket: number;
  startAge: number;
  endAge: number;
  incomeOption: string;
  loanInterestType: string;
  paymentFrequency: string;
  maxFaceAmount: number;
}

export interface InsuredDetails extends Partial<Illustration> {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  state: string;
  taxBracket: number;
  isEdit: boolean;
}

export interface QuestionnaireSnapshotData extends RouterData {
  questionnaire: any;
}

export interface SnapshotDataWithTitle extends RouterData {
  title: string;
}

export enum ClientReapplyResponseType {
  SUCCESS = 'SUCCESS',
  INACTIVE_PRIMARY_ADVISOR = 'INACTIVE_PRIMARY_ADVISOR',
  GENERIC_ERRORS = 'GENERIC_ERRORS',
}

export enum PaymentModalType {
  PAYMENT_AUTHORIZATION = 'PAYMENT_AUTHORIZATION',
  PAYMENT_RETRY = 'PAYMENT_RETRY',
}

export interface CACPostRiderDTO {
  adbr: boolean;
  waiverOfPremium: boolean;
}
